import { CssBaseline } from "@mui/material";
import { PaletteMode, ThemeProvider } from "@mui/material/styles";
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { createLyproTheme } from "../../theme";

const THEME_STORAGE_KEY = "lypro_theme_mode";

type AppThemeContextValue = {
  mode: PaletteMode;
  isDarkMode: boolean;
  setColorMode: (mode: PaletteMode) => void;
  toggleColorMode: () => void;
};

const AppThemeContext = createContext<AppThemeContextValue | undefined>(
  undefined
);

const getStoredMode = (): PaletteMode => {
  try {
    const storedMode = window.localStorage.getItem(THEME_STORAGE_KEY);
    return storedMode === "light" || storedMode === "dark"
      ? storedMode
      : "dark";
  } catch {
    return "dark";
  }
};

export const AppThemeProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [mode, setMode] = useState<PaletteMode>(getStoredMode);

  useEffect(() => {
    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, mode);
      document.documentElement.dataset.theme = mode;
    } catch {
      // The in-memory preference still works when storage is unavailable.
    }
  }, [mode]);

  const theme = useMemo(() => createLyproTheme(mode), [mode]);

  const contextValue = useMemo<AppThemeContextValue>(
    () => ({
      mode,
      isDarkMode: mode === "dark",
      setColorMode: setMode,
      toggleColorMode: () =>
        setMode((currentMode) => (currentMode === "dark" ? "light" : "dark")),
    }),
    [mode]
  );

  return (
    <AppThemeContext.Provider value={contextValue}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </AppThemeContext.Provider>
  );
};

export const useColorMode = () => {
  const context = useContext(AppThemeContext);

  if (!context) {
    throw new Error("useColorMode must be used inside AppThemeProvider");
  }

  return context;
};
